<template>
  <div>
    <BaseTitle :title="$t(edit ? 'content.edit_discussion' : 'content.add_discussion')" :yearPeriode="titleData" />
    <v-row>
      <v-col cols="12" md="12">
        <form @submit.prevent="submit">
          <v-card class="mb-8">
            <div class="pa-8">
              <form-group :name="$t('content.subject')" :validator="$v.form.subject">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="form.subject"
                    :disabled="isEditable"
                    v-bind="attrs"
                    :label="$t('content.subject')"
                    @input="$v.form.subject.$touch()"
                    @blur="$v.form.subject.$touch()"
                  />
                </template>
              </form-group>
              <div>
                <v-row>
                  <v-col cols="6" md="6">
                    <form-group :name="$t('content.available_from')" :validator="$v.form.availableFromDate">
                      <template slot-scope="{ attrs }">
                        <v-menu
                          ref="menuFromDate"
                          v-model="menuFromDate"
                          :close-on-content-click="false"
                          :return-value.sync="menuFromDate"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template #activator="{ on, attrs:attrActivator }">
                            <v-text-field
                              v-model="form.availableFromDate"
                              :label="$t('content.available_from')"
                              readonly
                              v-bind="{ ...attrActivator, ...attrs }"
                              v-on="on"
                            >
                              <template #append>
                                <svg-icon icon="calendar" class="primary--text mr-3" />
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker v-model="form.availableFromDate" :locale="$i18n.locale">
                            <v-spacer />
                            <v-btn text color="primary" @click="menuFromDate = false">{{ $t('cancel') }}</v-btn>
                            <v-btn text color="primary" @click="$refs.menuFromDate.save(form.availableFromDate)">{{ $t('ok') }}</v-btn>
                          </v-date-picker>
                        </v-menu>
                      </template>
                    </form-group>
                  </v-col>
                  <v-col cols="6" md="4">
                    <form-group :name="$t('content.available_time')" :validator="$v.form.availableFromTime" v-if="manualFrom === false" no-gutter>
                      <template slot-scope="{ attrs }">
                        <v-menu
                          ref="menuFromTime"
                          v-model="menuFromTime"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          :return-value.sync="menuFromTime"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs: attrActivator }">
                            <v-text-field
                              v-model="form.availableFromTime"
                              :label="$t('content.available_time')"
                              readonly
                              v-bind="{ ...attrActivator, ...attrs }"
                              v-on="on"
                              :disabled="!form.availableFromDate"
                            >
                              <template #append>
                                <svg-icon icon="clock" class="primary--text mr-3" />
                              </template>
                            </v-text-field>
                          </template>
                          <v-time-picker
                            v-model="form.availableFromTime"
                            :locale="$i18n.locale"
                            format="24hr"
                            @click:minute="$refs.menuFromTime.save(form.availableFromTime)"
                            :disabled="!form.availableFromDate"
                          />
                        </v-menu>
                      </template>
                    </form-group>

                    <form-group v-if="manualFrom === true" :name="$t('content.available_time')" :validator="$v.form.availableFromTime">
                      <template slot-scope="{ attrs }">
                        <v-text-field
                          type="time"
                          v-model="form.availableFromTime"
                          v-bind="{ ...attrs }"
                          :label="$t('content.available_time')"
                          :disabled="!form.availableFromDate"
                        />
                      </template>
                    </form-group>
                  </v-col>
                  <v-col cols="6" md="2">
                    <form-group>
                      <div class="d-flex align-center justify-space-between">
                        <v-switch v-model="manualFrom" />
                      </div>
                    </form-group>
                  </v-col>
                </v-row>
                <form-group :name="$t('form.status')" :validator="$v.form.hide">
                  <template slot-scope="{ attrs }">
                    <v-select
                      v-model="form.hide"
                      v-bind="attrs"
                      :disabled="isEditable"
                      @input="$v.form.hide.$touch()"
                      @blur="$v.form.hide.$touch()"
                      :label="$t('form.status')"
                      :items="lockItems"
                    />
                  </template>
                </form-group>
              </div>
              <div class="app-section-title mb-6">{{ $t('form.desc') }}</div>
              <form-group :name="$t('form.desc')" :validator="$v.form.description">
                <template slot-scope="{ attrs }">
                  <text-editor v-bind="attrs" v-model="form.description" @input="$v.form.description.$touch()" @blur="$v.form.description.$touch()" />
                </template>
              </form-group>
            </div>
          </v-card>
          <div class="d-flex justify-end align-center">
            <v-btn color="primary" class="btn-primary-custom mr-3" outlined @click="back">{{ $t('cancel') }}</v-btn>
            <v-btn color="primary" class="btn-primary-custom" @click="submit" :loading="loading">
              {{ $t('submit') }}
            </v-btn>
          </div>
        </form>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import TextEditor from '@/components/base/TextEditor.vue';
import BaseTitle from '@/components/base/Title';
import { requiredIf } from 'vuelidate/lib/validators';
import { maxLength, required } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
export default {
  name: 'CreateEditDiscussion',
  components: {
    BaseTitle,
    TextEditor
  },
  props: {
    isClass: {
      type: Boolean,
      default: false
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    isStudent: {
      type: Boolean,
      default: false
    }
  },
  validations() {
    return {
      form: {
        subject: { required, maxLength: maxLength(255) },
        description: { required },
        availableFromDate: {
          required: requiredIf(function() {
            return !!this.form.availableFromTime;
          })
        },

        availableFromTime: {
          required: requiredIf(function() {
            return !!this.form.availableFromDate;
          })
        },
        hide: { required }
      }
    };
  },
  computed: {
    ...mapGetters({
      classLevel: 'QuizTeacher/classLevel',
      tokenBearer: 'Auth/token',
      user: 'Auth/user',
      nameCourse: 'CourseList/nameCourse'
    }),
    lockItems() {
      return [
        { text: this.$t('paginate.show'), value: false },
        { text: this.$t('content.hide'), value: true }
      ];
    }
  },
  data: () => ({
    menuFromDate: false,
    menuFromTime: false,
    form: {
      subject: '',
      description: '',
      availableFromDate: '',
      availableFromTime: '',
      availableFrom: '',
      hide: ''
    },
    manualFrom: false,
    loadingButton: false,
    dataClass: '',
    loading: false,
    edit: false,
    titleData: ''
  }),
  methods: {
    getData() {
      this.$store.dispatch('Discussion/fetchDetail', this.$route.params.id).then(ras => {
        Object.keys(this.form).forEach(form => {
          this.form[form] = ras[form];
        });
      });
    },
    getDataGradeId() {
      this.loading = true;
      this.$store
        .dispatch('QuizTeacher/fetchListGradeId', { classId: this.$route.params.classId, courseId: this.$route.params.courseId })
        .then(res => {
          this.form.gradeId = res.gradeId;
        })
        .finally(() => (this.loading = false));
    },
    getDataClass() {
      if (!this.$route.params.classSectionId) {
        this.$store
          .dispatch('Class/fetchClassroomCourseName', { classId: this.$route.params.classId, courseId: this.$route.params.courseId })
          .then(res => {
            if (this.user.cerdasTypeId === 2 || this.user.cerdasTypeId === null) {
              this.$store.dispatch('SchoolClass/fetchDetail', this.$route.params.classId).then(resa => {
                this.dataClass = resa;
                this.titleData = res + ` ( ${resa.yearPeriode} ) `;
              });
            } else {
              this.$store.dispatch('Class/fetchListLiteDetail', this.$route.params.classId).then(resa => {
                this.dataClass = resa;
                this.titleData = res + ` ( ${resa.yearPeriode} ) `;
              });
            }
          });
      } else {
        this.$store.dispatch('ClassSection/getListDetail', { classSectionId: this.$route.params.classSectionId }).then(res => {
          if (this.user.cerdasTypeId === 2 || this.user.cerdasTypeId === null) {
            this.$store.dispatch('SchoolClass/fetchDetail', this.$route.params.classId).then(resa => {
              this.dataClass = resa;
              this.titleData = res.classroomName + ` ${res.numberClass}` + ` ( ${resa.yearPeriode} ) `;
            });
          }
        });
      }
    },
    getClassLevel() {
      this.$store.dispatch('QuizTeacher/fetchClassLevelOrCourse');
    },
    submit() {
      if (!this.form.description) {
        this.$store.dispatch('general/showErrorNotification', { message: this.$t('alert.alert_form_desc') });
      }
      this.$v.$touch();
      if (this.$v.$invalid) {
        return false;
      }

      if (this.form.availableFromDate) {
        this.form.availableFrom = this.form.availableFromDate.concat(' ', this.form.availableFromTime);
      }

      const input = {
        subject: this.form.subject,
        description: this.form.description,
        classroomId: this.$route.params.classId,
        courseId: this.$route.params.courseId,
        availableFrom: this.form.availableFrom,
        hide: this.form.hide,
        classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : ''
      };

      this.loading = true;
      const payload = { ...(this.edit ? { id: this.$route.params.id } : null), ...input };

      this.$store
        .dispatch(`Discussion/${this.edit ? 'update' : 'save'}`, payload)
        .then(() =>
          this.$router.push({
            name:
              this.edit && !this.$route.params.classSectionId
                ? 'DiscussionTeacherUpdateSuccess'
                : !this.edit && !this.$route.params.classSectionId
                ? 'DiscussionTeacherSuccess'
                : this.edit && this.$route.params.classSectionId
                ? 'DiscussionSectionTeacherUpdateSuccess'
                : 'DiscussionSectionTeacherSuccess'
          })
        )
        .finally(() => (this.loading = false));
    },
    back() {
      if (this.$route.params.classSectionId) {
        this.$router.push({ name: 'DiscussionSectionIndexClass' });
      } else {
        this.$router.push({ name: 'DiscussionIndexClass' });
      }
    }
  },
  created() {
    if (this.$route.params.id) {
      this.getData();
      this.edit = true;
    }
    if (this.isClass) {
      this.getDataGradeId();
      this.getDataClass();
    } else {
      this.getClassLevel();
      localStorage.removeItem('courseName');
    }
  }
};
</script>

<style lang="scss">
html,
body {
  height: 100%;
}

body {
  height: 100%;
}
</style>
